/**
 * pageCache — Caché en memoria para navegación instantánea (LRU + TTL bounded).
 *
 * Patrón stale-while-revalidate con límites duros:
 *  - MAX_ENTRIES: 50 entradas máximo. Si se llena, se evicta la MENOS recientemente usada.
 *  - Cada escritura limpia entradas expiradas (amortizado — evita que la memoria crezca).
 *  - Cada entrada tiene su propio TTL individual.
 *
 * El caché vive mientras la pestaña del browser esté abierta.
 * Si el usuario cierra/recarga la pestaña, el caché se limpia (comportamiento correcto).
 */

const MAX_ENTRIES = 50;
const _cache = new Map();

/** Purga entradas expiradas. Retorna cuántas se eliminaron. */
function _purgeExpired() {
  const now = Date.now();
  let removed = 0;
  for (const [key, entry] of _cache) {
    if (now > entry.expiresAt) {
      _cache.delete(key);
      removed += 1;
    }
  }
  return removed;
}

/** Evicta la entrada LRU (más antigua en inserción/acceso). Map preserva orden. */
function _evictLRU() {
  const oldestKey = _cache.keys().next().value;
  if (oldestKey !== undefined) _cache.delete(oldestKey);
}

/**
 * Obtiene datos del caché. Retorna null si no existe o expiró.
 * Al acceder, mueve la entrada al final (LRU: "recently used").
 */
export function getPageCache(key) {
  const entry = _cache.get(key);
  if (!entry) return null;
  if (Date.now() > entry.expiresAt) {
    _cache.delete(key);
    return null;
  }
  // LRU bump: reinsert to move to end
  _cache.delete(key);
  _cache.set(key, entry);
  return entry.data;
}

/**
 * Guarda datos en el caché con TTL.
 * Aplica purga de expiradas y evicción LRU si se excede MAX_ENTRIES.
 */
export function setPageCache(key, data, ttlMs = 60000) {
  // Purga oportunista (barato, O(n) pero n ≤ 50)
  _purgeExpired();
  // Si aún estamos al tope, evictar LRU
  while (_cache.size >= MAX_ENTRIES) {
    _evictLRU();
  }
  _cache.set(key, {
    data,
    expiresAt: Date.now() + ttlMs,
  });
}

/**
 * Invalida todas las entradas de caché que empiecen con el prefijo dado.
 * Útil para invalidar después de una operación de escritura.
 */
export function invalidatePageCache(prefix) {
  for (const key of _cache.keys()) {
    if (key.startsWith(prefix)) {
      _cache.delete(key);
    }
  }
}

/** Limpia todo el caché (útil al cambiar de empresa o cerrar sesión). */
export function clearAllPageCache() {
  _cache.clear();
}

/** Diagnóstico — para debugging / futuro badge admin. */
export function getPageCacheStats() {
  return {
    size: _cache.size,
    max: MAX_ENTRIES,
    keys: Array.from(_cache.keys()),
  };
}
